import * as Android from '@/api/android';
import * as Server from '@/api/server';
import MenuOption from '@/components/MenuOption.vue';
import LeftNavCartMenuOption from '@/components/templates/left-nav-cart/LeftNavCartMenuOption.vue';
import UserError from '@/error';
import i18n from '@/i18n';
import CallMessage from '@/interfaces/CallMessage';
import LimitMenu from '@/interfaces/LimitMenu';
import Menu from '@/interfaces/Menu';
import MenuGroup from '@/interfaces/MenuGroup';
import ResponseEvent from '@/interfaces/ResponseEvent';
import ResponseEventItem from '@/interfaces/ResponseEventItem';
import ResponseItem from '@/interfaces/ResponseItem';
import ResponseMenuData from '@/interfaces/ResponseMenuData';
import SetMenu from '@/interfaces/SetMenu';
import SetMenuGroup from '@/interfaces/SetMenuGroup';
import Topping from '@/interfaces/Topping';
import ToppingGroup from '@/interfaces/ToppingGroup';
import { useAdStore } from '@/stores/ad';
import { useCommonStore } from '@/stores/common';
import { useConfigStore } from '@/stores/config';
import { useOrderStore } from '@/stores/order';
import { checkUseTime, generateEventMap, getMenuTaxAmount } from '@/utils';
import { defineStore } from 'pinia';
import { computed, markRaw, ref } from 'vue';

export const useMenuStore = defineStore('menu', () => {
  const commonStore = useCommonStore();
  const configStore = useConfigStore();
  const orderStore = useOrderStore();
  const adStore = useAdStore();

  let itemMap: Map<string, ResponseItem>;

  const menuGroups = ref<MenuGroup[]>([]);
  const menus = ref<Menu[]>([]);
  const toppingGroups = ref<ToppingGroup[]>([]);
  const toppings = ref<Topping[]>([]);
  const setMenuGroups = ref<SetMenuGroup[]>([]);
  const setMenus = ref<SetMenu[]>([]);
  const callMessages = ref<CallMessage[]>([]);
  const limitMenus = ref<LimitMenu[]>([]);
  // 활성화된 메뉴 그룹 인덱스 (좌측 네비게이션 전용)
  const activeIndex = ref(0);

  // #region ============================================== Getters ==============================================
  /**
   * 사용 시간 옵션이 적용된 메뉴 그룹 리스트 조회
   * @returns 필터링된 메뉴 그룹 리스트
   */
  const filteredMenuGroups = computed(() => {
    // 리셋이 되면 메뉴그룹 사용시간 적용을 위해
    commonStore.resetKey;
    return menuGroups.value.filter((menuGroup) => {
      // 메뉴가 하나도 없는 경우 필터링
      if (menus.value.filter((menu) => menu.GroupCode === menuGroup.GroupCode).length === 0) return false;

      // 메뉴 그룹 사용시간으로 필터링
      if (menuGroup.UseType === '1') {
        // 시간대별 사용
        return checkUseTime(menuGroup.StartUseTime, menuGroup.EndUseTime);
      } else if (menuGroup.UseType === '2') {
        // 요일별 사용
        // 저장된 시간 설정이 없는 경우 사용
        if (!menuGroup.UseTime) return true;

        // 현재 요일 시간설정 조회
        const useTime = menuGroup.UseTime[new Date().getDay()];

        // 요일 설정을 사용하지 않는 경우 사용
        if (!useTime.IsUsed) return true;
        return checkUseTime(useTime.StartTime, useTime.EndTime);
      }
      return true;
    });
  });
  // #endregion

  // #region ============================================== Set Actions ==========================================
  /**
   * 메뉴 데이터 세팅
   * @param data API에서 조회한 메뉴 데이터
   */
  const setMenuData = (data: ResponseMenuData) => {
    // Item데이터를 Map으로 변환하여 저장
    itemMap = new Map(data.Items?.map((item) => [item.ItemCode, item]));

    // 메뉴 데이터 정렬 적용
    applyMenuDataSorting(data);

    // 메뉴 그룹 사용시간 JSON으로 파싱
    const mappedMenuGroups = data.MenuGroups.map((dataMenuGroup) => {
      const menuGroup: MenuGroup = {
        ...dataMenuGroup,
        UseTime: dataMenuGroup.UseTimeString && JSON.parse(dataMenuGroup.UseTimeString),
      };
      return menuGroup;
    });

    // 메뉴 리스트 생성
    const mappedMenus = data.Menus.reduce<Menu[]>((acc, dataMenu) => {
      // 아이템 조회
      const item = getItem(dataMenu.ItemCode);

      // 아이템이 없는 경우(HIDE) 필터링
      if (!item) return acc;

      const menu: Menu = {
        ...dataMenu,
        ...item,
        FinalPrice: 0,
        DiscountAmount: 0,
        DiscountPercentage: 0,
      };

      adStore.applyAdImagePath(menu, mappedMenuGroups);

      acc.push(menu);
      return acc;
    }, []);

    // 세트메뉴 리스트 생성
    const mappedSetMenus = data.SetMenus.reduce<SetMenu[]>((acc, dataSetMenu) => {
      const item = getItem(dataSetMenu.SetMenuCode);

      // 아이템이 없는 경우(HIDE) 필터링
      if (!item) return acc;

      acc.push({
        ...dataSetMenu,
        Name: item.Name,
        NameEn: item.NameEn,
        NameKo: item.NameKo,
        NameJa: item.NameJa,
        NameZh: item.NameZh,
        IsSoldOut: item.IsSoldOut,
      });
      return acc;
    }, []);

    // 메뉴 단가 및 행사 할인 적용
    applyMenuPrice(mappedMenus, data.Events, data.EventItems);

    menuGroups.value = markRaw(mappedMenuGroups);
    menus.value = mappedMenus; // 품절 처리를 위해 메뉴 리스트에는 markRaw 제거하여 반응형으로 사용
    toppingGroups.value = markRaw(data.ToppingGroups);
    toppings.value = markRaw(data.Toppings);
    setMenuGroups.value = markRaw(data.SetMenuGroups);
    setMenus.value = markRaw(mappedSetMenus);
    callMessages.value = markRaw(data.CallMessages);
  };

  /**
   * 메뉴 한정수량 리스트 설정
   * @param limitMenus 메뉴 한정수량 리스트
   */
  const setLimitMenus = (_limitMenus: LimitMenu[]) => {
    limitMenus.value = _limitMenus;
  };

  const fetchSoldOut = async () => {
    // 메뉴 일반 품절 데이터 조회 및 적용
    const soldoutData = await Server.getSoldoutData();

    menus.value.forEach((menu) => {
      menu.IsSoldOut = soldoutData.includes(menu.ItemCode);
    });
    toppings.value.forEach((topping) => {
      topping.IsSoldOut = soldoutData.includes(topping.ItemCode);
    });
    setMenus.value.forEach((setMenu) => {
      setMenu.IsSoldOut = soldoutData.includes(setMenu.SetMenuCode);
    });
  };

  /** 품절 및 한정수량 조회하여 업데이트 */
  const fetchLimitItem = async () => {
    // 메뉴 한정 품절 데이터 조회 및 적용
    if (configStore.config.IsLimitedItemUsed) {
      const response = await Android.getOrderLimitItemsForOrderAsync();
      if (!response) return;

      if (response.RET_CODE === 0) {
        setLimitMenus(response.DATA);
      } else {
        throw new UserError(i18n.global.t('error.fail_limit_item'));
      }
    }
  };

  /** 활성화 메뉴 그룹 인덱스 세팅 */
  const setActiveIndex = (index: number) => {
    activeIndex.value = index;
  };

  /**
   * 전체 데이터 조회 후 세팅
   * @param forceReload 강제 재조회 여부
   */
  const setData = async (forceReload: boolean = false) => {
    if (menuGroups.value.length === 0 || forceReload) {
      // API에서 메뉴 데이터 조회
      let responseData;
      if (
        configStore.isMobileLayout &&
        configStore.config.MobileMenupanCode &&
        configStore.config.MobileMenupanCode !== '0001'
      ) {
        responseData = await Server.getMenuData(configStore.config.MobileMenupanCode);
      } else {
        responseData = await Server.getMenuData(configStore.config.MenupanCode);
      }
      const data = JSON.parse(JSON.stringify(responseData));

      // 광고 조회 및 설정
      const ads = await Server.getAds();
      adStore.setAds(ads);

      // 메뉴 스토어에 데이터 세팅
      setMenuData(data);

      // POS에서 한정수량 데이터 조회
      if (configStore.config.IsLimitedItemUsed) {
        const response = await Android.getOrderLimitItemsForOrderAsync();
        if (response?.RET_CODE === 0) setLimitMenus(response.DATA);
      }
    }
  };

  // #endregion

  // #region ============================================== Get Actions ==========================================
  /** 아이템 조회 */
  const getItem = (itemCode: string) => {
    return itemMap.get(itemCode);
  };

  /**
   * 메뉴 단일 데이터 조회
   * @param groupCode 메뉴 그룹 코드
   * @param itemCode 메뉴 아이템 코드
   * @returns 메뉴 데이터
   */
  const getMenu = (groupCode: string | null, itemCode: string): Menu => {
    const menu = menus.value.find(
      (menu) => (groupCode ? menu.GroupCode === groupCode : true) && menu.ItemCode === itemCode,
    );
    if (!menu) throw new UserError(`메뉴 데이터를 찾을 수 없습니다.`);
    return menu;
  };

  /**
   * 메뉴에 적용되는 단가를 반환. 특정 조건 만족 시 경감 세금 단가를 반환
   *
   * **경감 세금 단가 조건**
   * - 일본 문화권 (`ja-JP`) 매장일 때
   * - 매장 설정에서 경감 세금 사용 여부가 `true`일 때
   * - 메뉴에서 경감 세금 항목(`IsTax2`)이 `true`일 때
   * - 메뉴에 경감 세금용 단가(`ReducePrice`)가 설정되어 있을 때
   * @param menu - 메뉴 데이터
   * @returns 메뉴의 단가
   */
  const getMenuPrice = (menu: Menu) => {
    let price = menu.Price;
    // 일본 경감세금 사용 여부 체크
    if (
      configStore.config.CultureName === 'ja-JP' &&
      configStore.config.IsReduceTaxUsed &&
      menu.IsTax2 &&
      menu.ReducePrice
    ) {
      price = menu.ReducePrice;
    }

    return price;
  };

  /**
   * 메뉴 총 금액 조회
   * @param menu 메뉴 데이터
   * @param addPrice 토핑/세트메뉴 추가 금액
   * @param qty 수량
   * @param isTaxIncluded 세금 별도인 경우 세금 포함 여부
   * @returns 최종 메뉴 가격
   */
  const getMenuAmount = (
    menuPrice: number,
    addPrice: number,
    qty: number,
    isTaxIncluded: boolean,
    isMenuTax1Used: boolean,
    isMenuTax2Used: boolean,
  ) => {
    // 추가 금액 합산 후 수량 적용
    let menuAmount = (menuPrice + addPrice) * qty;

    /**
     * 경감세금 사용여부 체크
     * 일본 문화권 && 홀서빙 경감세금 사용 && 메뉴 경감세금 사용 3가지 모두 충족 시 경감세금 적용
     */
    if (configStore.config.CultureName === 'ja-JP' && configStore.config.IsReduceTaxUsed && isMenuTax2Used) {
      // 경감 세금 설정이 '별도'이고 '세금 포함' 옵션을 사용하는 경우 메뉴 가격에 세금 합산
      if (configStore.config.TaxType2 === '1' && isTaxIncluded) {
        menuAmount += getMenuTaxAmount(menuAmount, isMenuTax1Used, isMenuTax2Used);
      }
    } else {
      // 일반 세금 설정이 '별도'이고 '세금 포함' 옵션을 사용하는 경우 메뉴 가격에 세금 합산
      if (configStore.config.TaxType1 === '1' && isTaxIncluded) {
        menuAmount += getMenuTaxAmount(menuAmount, isMenuTax1Used, isMenuTax2Used);
      }
    }

    return menuAmount;
  };

  /**
   * 템플릿 설정에 따라 메뉴 옵션 컴포넌트를 반환
   */
  const getMenuOptionComponent = () => {
    return configStore.config.Template === 'left-nav-cart' ? LeftNavCartMenuOption : MenuOption;
  };

  /**
   * 토핑메뉴 그룹 단일 조회
   * @param toppingGroups 토핑 그룹 리스트
   * @param toppingTemplateCode 토핑 템플릿 코드
   * @param groupCode 토핑 그룹 코드
   * @returns 토핑메뉴 그룹 데이터
   */
  const getToppingGroup = (groupCode: string): ToppingGroup => {
    const toppingGroup = toppingGroups.value.find((toppingGroup) => toppingGroup.GroupCode === groupCode);

    if (!toppingGroup) throw new UserError('토핑 그룹 데이터를 찾을 수 없습니다.');

    return toppingGroup;
  };

  /**
   * 토핑 단일 데이터 조회
   * @param toppings 토핑 리스트
   * @param toppingGroupCode 토핑 그룹 코드
   * @param toppingCode 토핑 코드
   * @returns 토핑 데이터
   */
  const getTopping = (toppingGroupCode: string, toppingCode: string): Topping => {
    const topping = toppings.value.find(
      (topping) => topping.GroupCode === toppingGroupCode && topping.ToppingCode === toppingCode,
    );

    if (!topping) throw new UserError('토핑 데이터를 찾을 수 없습니다.');

    return topping;
  };

  /**
   * 토핑 추가 금액 조회
   * @param topping 토핑 데이터
   * @param isTaxUsed2 세금2 사용여부
   * @returns 토핑 추가 금액
   */
  const getToppingPrice = (topping: Topping, isTaxUsed2: boolean) => {
    if (configStore.config.CultureName === 'ja-JP' && configStore.config.IsReduceTaxUsed && isTaxUsed2) {
      if (topping.AddReducePrice > 0) {
        return topping.AddReducePrice;
      }
    }
    return topping.AddPrice;
  };

  /**
   * 세트메뉴 그룹 단일 조회
   * @param setMenuGroups 세트메뉴 그룹 리스트
   * @param itemCode 세트메뉴 아이템 코드
   * @param groupCode 세트메뉴 그룹코드
   * @returns 세트메뉴 그룹 데이터
   */
  const getSetMenuGroup = (itemCode: string, groupCode: number): SetMenuGroup => {
    const setMenuGroup = setMenuGroups.value.find(
      (setMenuGroup) => setMenuGroup.ItemCode === itemCode && setMenuGroup.GroupCode === groupCode,
    );

    if (!setMenuGroup) throw new UserError('세트메뉴 그룹 데이터를 찾을 수 없습니다.');

    return setMenuGroup;
  };

  /**
   * 세트메뉴 단일 조회
   * @param setMenus 세트메뉴 리스트
   * @param itemCode 세트메뉴 아이템 코드
   * @param groupCode 세트메뉴 그룹 코드
   * @param setMenuCode 세트메뉴 코드
   * @returns 세트메뉴 데이터
   */
  const getSetMenu = (itemCode: string, groupCode: number, setMenuCode: string): SetMenu => {
    const setMenu = setMenus.value.find(
      (setMenu) =>
        setMenu.ItemCode === itemCode && setMenu.GroupCode === groupCode && setMenu.SetMenuCode === setMenuCode,
    );

    if (!setMenu) throw new UserError('세트메뉴 데이터를 찾을 수 없습니다.');

    return setMenu;
  };

  /**
   * 세트메뉴 추가 금액 조회
   * @param setMenu 세트메뉴 데이터
   * @param isTaxUsed2 세금2 사용여부
   * @returns 세트메뉴 추가 금액
   */
  const getSetMenuPrice = (setMenu: SetMenu, isTaxUsed2: boolean) => {
    if (configStore.config.CultureName === 'ja-JP' && configStore.config.IsReduceTaxUsed && isTaxUsed2) {
      if (setMenu.AddReducePrice > 0) {
        return setMenu.AddReducePrice;
      }
    }
    return setMenu.AddPrice;
  };

  /**
   * 한정수량 아이템 리스트에서 일치하는 첫번째 아이템을 찾아서 반환
   * @param itemCode 아이템 코드
   * @returns 일치하는 첫번째 한정수량 아이템 데이터
   */
  const getLimitItem = (itemCode: string) => {
    return limitMenus.value.find((limitItem) => limitItem.ItemCode === itemCode);
  };
  // #endregion

  // #region ============================================== Private Functions ====================================
  /**
   * 메뉴 데이터에 단가 및 할인 적용
   * @param menus 메뉴 데이터
   * @param events 행사 데이터
   * @param eventItems 행사 아이템 데이터
   */
  const applyMenuPrice = (menus: Menu[], events: ResponseEvent[], eventItems: ResponseEventItem[]) => {
    // 빠른 조회를위해 이벤트를 Map으로 변환
    const eventMap = generateEventMap(events, eventItems);

    menus.forEach((menu) => {
      // 메뉴 적용 단가 조회
      const price = getMenuPrice(menu);

      // 기본값 설정
      menu.FinalPrice = price;

      // 행사 조회
      const event = eventMap.get(menu.ItemCode);
      if (!event) return;

      if (event.EventType === '0') {
        // 행사단가할인 (행사에서 메뉴별로 지정한 가격으로 판매)
        menu.DiscountAmount = price - event.EventPrice;
        menu.DiscountPercentage = Math.round((menu.DiscountAmount / price) * 100);
        menu.DiscountType = event.DiscountType;
        menu.DiscountSection = 'DT598';
        menu.EventCode = event.EventCode;
        menu.EventName = event.EventName;
        menu.EventType = event.EventType;
      } else if (event.EventType === '2' && event.DiscountType === '0' && event.DiscountAmount <= price) {
        // 품목단위할인 - 금액할인 (행사에서 정한 금액 만큼 할인. 할인 금액이 더 큰 경우 할인을 적용하지 않는다)
        menu.DiscountAmount = event.DiscountAmount;
        menu.DiscountPercentage = Math.round((menu.DiscountAmount / price) * 100);
        menu.DiscountType = event.DiscountType;
        menu.DiscountSection = 'DT598';
        menu.EventCode = event.EventCode;
        menu.EventName = event.EventName;
        menu.EventType = event.EventType;
      } else if (event.EventType === '2' && event.DiscountType === '1') {
        // 품목단위할인 - 비율할인 (행사에서 정한 비율 만큼 할인. 메뉴 단가만 할인 적용하고 추가 금액은 적용하지 않는다)
        menu.DiscountAmount = Math.floor(price * event.DiscountRate);
        menu.DiscountPercentage = event.DiscountRate * 100;
        menu.DiscountType = event.DiscountType;
        menu.DiscountSection = 'DT598';
        menu.EventCode = event.EventCode;
        menu.EventName = event.EventName;
        menu.EventType = event.EventType;
      }

      // 할인 적용 후 최종 단가 계산
      if (menu.DiscountAmount) {
        menu.FinalPrice = price - menu.DiscountAmount;
      }
    });
  };

  /**
   * 메뉴 데이터에 정렬 적용
   * @param menuData 메뉴 데이터
   * @returns
   */
  const applyMenuDataSorting = (menuData: ResponseMenuData) => {
    if (!menuData) return;

    // 좌표값에 따라 정렬
    menuData.MenuGroups.sort(
      (a, b) => a.PositionZ - b.PositionZ || a.PositionY - b.PositionY || a.PositionX - b.PositionX,
    );
    menuData.Menus.sort((a, b) => a.PositionZ - b.PositionZ || a.PositionY - b.PositionY || a.PositionX - b.PositionX);
    menuData.ToppingGroups.sort((a, b) => a.PositionZ - b.PositionZ || a.PositionX - b.PositionX);
    menuData.Toppings.sort(
      (a, b) => a.PositionZ - b.PositionZ || a.PositionY - b.PositionY || a.PositionX - b.PositionX,
    );
    menuData.SetMenuGroups.sort((a, b) => a.GroupCode - b.GroupCode);
    menuData.SetMenus.sort(
      (a, b) =>
        Number(a.ItemCode) - Number(b.ItemCode) ||
        a.GroupCode - b.GroupCode ||
        Number(a.SetMenuCode) - Number(b.SetMenuCode),
    );
  };
  // #endregion =======================

  return {
    menuGroups,
    menus,
    toppingGroups,
    toppings,
    setMenuGroups,
    setMenus,
    callMessages,
    limitMenus,
    activeIndex,
    filteredMenuGroups,

    setMenuData,
    setLimitMenus,
    fetchSoldOut,
    fetchLimitItem,
    setActiveIndex,
    setData,

    getItem,
    getMenu,
    getMenuPrice,
    getMenuAmount,
    getMenuOptionComponent,
    getToppingGroup,
    getTopping,
    getToppingPrice,
    getSetMenuGroup, // 사용안함?
    getSetMenu,
    getSetMenuPrice,
    getLimitItem,
  };
});
